/**
 * Registro central de sonidos de Aureum (data-driven).
 *
 * Fuente de verdad del catálogo de audio: cada sonido se declara una vez acá con su `category`
 * (bus), su `group` (nivel básico de agrupación para el visor in-game) y su `file` (ruta relativa
 * en `client/assets/audio/`, SIN extensión; el `AudioManager` prueba `.ogg` y cae a `.mp3`).
 *
 * El sistema NO rompe si un id no tiene archivo cargado todavía: `AudioManager.play` de un id sin
 * buffer es un no-op silencioso. La idea es dejar TODOS los enganches cableados y que el equipo
 * vaya cargando los archivos después (ver docs/SOUND_AND_MUSIC.md).
 *
 * Convención (memoria: registros data-driven): las tablas de mapeo (superficie→paso, armadura→
 * impacto, subtipo de arma→golpe, etc.) son `Record<...>` con un DEFAULT explícito y overrides
 * OPCIONALES. La mayoría de las entidades NO tiene sonido propio: caen al default. Nunca
 * `if (id === 'x')`.
 */

import { WeaponSubtype, ArmorSubtype } from '../types/item';

// ============================================================================
// BUSES / CATEGORÍAS
// ============================================================================

/**
 * Buses de mezcla. Cada uno tiene volumen + toggle independientes en Opciones; todos default ON.
 * El `master` los agrupa a todos. Los buses "posicionales" pasan por el paneo/atenuación 3D
 * (footsteps/combat/spells/npc/ambient); `ui`/`music`/`heartbeat` son 2D directos al bus.
 */
export enum SoundCategory {
  MUSIC = 'music',
  FOOTSTEPS = 'footsteps',
  COMBAT = 'combat',
  SPELLS = 'spells',
  NPC = 'npc',
  AMBIENT = 'ambient',
  UI = 'ui',
  HEARTBEAT = 'heartbeat',
}

/** Categorías cuyo audio se posiciona en el mundo (paneo + atenuación por distancia). */
export const POSITIONAL_CATEGORIES: ReadonlySet<SoundCategory> = new Set([
  SoundCategory.FOOTSTEPS,
  SoundCategory.COMBAT,
  SoundCategory.SPELLS,
  SoundCategory.NPC,
  SoundCategory.AMBIENT,
]);

/** Orden + etiqueta ES de las categorías para la UI de Opciones. */
export const SOUND_CATEGORY_ORDER: ReadonlyArray<SoundCategory> = [
  SoundCategory.MUSIC,
  SoundCategory.AMBIENT,
  SoundCategory.FOOTSTEPS,
  SoundCategory.COMBAT,
  SoundCategory.SPELLS,
  SoundCategory.NPC,
  SoundCategory.UI,
  SoundCategory.HEARTBEAT,
];

export const SOUND_CATEGORY_LABELS_ES: Record<SoundCategory, string> = {
  [SoundCategory.MUSIC]: 'Música',
  [SoundCategory.AMBIENT]: 'Entorno',
  [SoundCategory.FOOTSTEPS]: 'Pasos',
  [SoundCategory.COMBAT]: 'Combate',
  [SoundCategory.SPELLS]: 'Hechizos',
  [SoundCategory.NPC]: 'NPCs',
  [SoundCategory.UI]: 'Interfaz',
  [SoundCategory.HEARTBEAT]: 'Latido',
};

// ============================================================================
// DEFINICIÓN DE SONIDO
// ============================================================================

export interface SoundDef {
  /** ID único (ej. 'footstep_grass', 'music_city', 'coin_big'). */
  id: string;
  /** Bus de mezcla. */
  category: SoundCategory;
  /** Agrupación de nivel básico para el visor in-game (ej. 'Pasos', 'Combate', 'UI'). */
  group: string;
  /** Etiqueta legible para el visor (ES). Si falta, se usa el id. */
  label?: string;
  /**
   * Ruta relativa dentro de `assets/audio/`, SIN extensión (el manager prueba `.ogg` y `.mp3`).
   * Ej. 'footsteps/grass' → assets/audio/footsteps/grass.ogg
   */
  file: string;
  /** Loop (música/ambiente/heartbeat). Default false. */
  loop?: boolean;
  /** Ganancia base propia del sonido (0..1). Default 1. */
  volume?: number;
  /**
   * Variantes: si está, en cada `play` se elige una al azar (paths sin extensión). Útil para
   * ruidos de entorno, voces de NPC, flechas. Si está presente, `file` es la variante por defecto.
   */
  variants?: string[];
}

// ============================================================================
// REGISTRO (arranca casi vacío — se va poblando al cargar archivos reales)
// ============================================================================

/**
 * Catálogo de sonidos. Arranca con unos pocos de prueba/estructura; el equipo suma entradas acá al
 * cargar los archivos en `client/assets/audio/`. Al agregar un sonido:
 *   1. Sumarlo acá con su category/group/file.
 *   2. Dropear el archivo en `assets/audio/<...>.ogg` (y opcional `.mp3`).
 *   3. Si aplica, referenciar su id desde la tabla de mapeo correspondiente (abajo) o desde el
 *      catálogo del contenido (spell.sounds, item.hitSound, npc.sounds, area.musicId, etc.).
 */
export const SOUNDS: Record<string, SoundDef> = {
  // --- Pasos por terreno ---
  // Terrenos con 6 variantes cada uno (`file` = variante 1; `variants` = 2..6). La variante que suena
  // se elige DETERMINISTA por paso (hash de entityId+tile+dir) para que todos los observadores
  // escuchen la misma. Ver docs/SOUND_AND_MUSIC.md §Footsteps deterministas.
  footstep_grass: {
    id: 'footstep_grass',
    category: SoundCategory.FOOTSTEPS,
    group: 'Pasos',
    label: 'Paso — pasto',
    file: 'footsteps/grass/1',
    variants: ['footsteps/grass/2', 'footsteps/grass/3', 'footsteps/grass/4', 'footsteps/grass/5', 'footsteps/grass/6'],
    volume: 0.7,
  },
  footstep_dirt: {
    id: 'footstep_dirt',
    category: SoundCategory.FOOTSTEPS,
    group: 'Pasos',
    label: 'Paso — tierra',
    file: 'footsteps/dirt/1',
    variants: ['footsteps/dirt/2', 'footsteps/dirt/3', 'footsteps/dirt/4', 'footsteps/dirt/5', 'footsteps/dirt/6'],
    volume: 0.7,
  },
  footstep_stone: {
    id: 'footstep_stone',
    category: SoundCategory.FOOTSTEPS,
    group: 'Pasos',
    label: 'Paso — piedra / camino',
    file: 'footsteps/stone/1',
    variants: ['footsteps/stone/2', 'footsteps/stone/3', 'footsteps/stone/4', 'footsteps/stone/5', 'footsteps/stone/6'],
    volume: 0.7,
  },
  footstep_wood: {
    id: 'footstep_wood',
    category: SoundCategory.FOOTSTEPS,
    group: 'Pasos',
    label: 'Paso — madera',
    file: 'footsteps/wood/1',
    variants: ['footsteps/wood/2', 'footsteps/wood/3', 'footsteps/wood/4', 'footsteps/wood/5', 'footsteps/wood/6'],
    volume: 0.7,
  },
  footstep_water: {
    id: 'footstep_water',
    category: SoundCategory.FOOTSTEPS,
    group: 'Pasos',
    label: 'Paso — agua (navegando)',
    file: 'footsteps/water/1',
    variants: ['footsteps/water/2', 'footsteps/water/3', 'footsteps/water/4', 'footsteps/water/5'],
    volume: 0.7,
  },

  // --- UI ---
  sfx_ui_hover: {
    id: 'sfx_ui_hover',
    category: SoundCategory.UI,
    group: 'Interfaz',
    label: 'UI — hover',
    file: 'ui/hover',
    volume: 0.5,
  },
  sfx_ui_click: {
    id: 'sfx_ui_click',
    category: SoundCategory.UI,
    group: 'Interfaz',
    label: 'UI — click',
    file: 'ui/click',
    volume: 0.6,
  },

  // --- Combate (defaults) ---
  // Golpe melee (swing): variantes de "sword slash". Suena en cada swing (onMeleeSwing, AOI).
  sfx_combat_melee: {
    id: 'sfx_combat_melee',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Golpe melee',
    file: 'combat/melee/1',
    variants: ['combat/melee/2', 'combat/melee/3', 'combat/melee/4', 'combat/melee/5'],
  },
  sfx_combat_ranged: {
    id: 'sfx_combat_ranged',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Disparo a distancia (default)',
    file: 'combat/ranged',
  },
  // Fallo de golpe físico (whoosh): melee/flechas que fallan, o cuando un NPC nos falla. 13 variantes.
  combat_miss: {
    id: 'combat_miss',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Fallo de golpe (whoosh)',
    file: 'combat/miss/1',
    variants: [
      'combat/miss/2', 'combat/miss/3', 'combat/miss/4', 'combat/miss/5', 'combat/miss/6',
      'combat/miss/7', 'combat/miss/8', 'combat/miss/9', 'combat/miss/10', 'combat/miss/11',
      'combat/miss/12', 'combat/miss/13',
    ],
  },
  sfx_combat_impact: {
    id: 'sfx_combat_impact',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Impacto (default)',
    file: 'combat/impact',
  },
  sfx_impact_heavy: {
    id: 'sfx_impact_heavy',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Impacto — armadura pesada',
    file: 'combat/impact_heavy/1',
    variants: ['combat/impact_heavy/2'],
  },
  sfx_impact_light: {
    id: 'sfx_impact_light',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Impacto — armadura ligera (pendiente)',
    file: 'combat/impact_light',
  },
  sfx_impact_cloth: {
    id: 'sfx_impact_cloth',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Impacto — tela (pendiente)',
    file: 'combat/impact_cloth',
  },
  // Bloqueo con escudo DEFAULT (3 variantes de "shield impact").
  sfx_shield_block: {
    id: 'sfx_shield_block',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Bloqueo con escudo (default)',
    file: 'combat/shield_block/1',
    variants: ['combat/shield_block/2', 'combat/shield_block/3'],
  },
  // Bloqueo con escudo de METAL (configurable para escudos pesados/metálicos). 3 variantes.
  shield_metal: {
    id: 'shield_metal',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Bloqueo — escudo de metal',
    file: 'combat/shield_metal/1',
    variants: ['combat/shield_metal/2', 'combat/shield_metal/3'],
  },

  // --- Estado / Skill ---
  sfx_bleeding: {
    id: 'sfx_bleeding',
    category: SoundCategory.COMBAT,
    group: 'Estado',
    label: 'Sangrado',
    file: 'status/bleeding',
  },
  sfx_pickaxe_mining: {
    id: 'sfx_pickaxe_mining',
    category: SoundCategory.NPC,
    group: 'Skilling',
    label: 'Pico minando',
    file: 'skill/pickaxe_mining',
    volume: 0.7,
  },
  // Resultado de cada intento de minería (server-authoritative, posicional en el player).
  // Archivos pendientes: son no-op silenciosos hasta que se carguen. Ver docs/MINING_AND_SMITHING.md.
  sfx_mining_fail: {
    id: 'sfx_mining_fail',
    category: SoundCategory.NPC,
    group: 'Skilling',
    label: 'Minado sin éxito',
    file: 'skill/mining_fail',
    volume: 0.6,
  },
  sfx_gem_found: {
    id: 'sfx_gem_found',
    category: SoundCategory.NPC,
    group: 'Skilling',
    label: 'Gema encontrada',
    file: 'skill/gem_found',
    volume: 0.8,
  },
  sfx_tool_break: {
    id: 'sfx_tool_break',
    category: SoundCategory.NPC,
    group: 'Skilling',
    label: 'Herramienta rota',
    file: 'skill/tool_break',
    volume: 0.8,
  },
  sfx_passive_point: {
    id: 'sfx_passive_point',
    category: SoundCategory.UI,
    group: 'Skilling',
    label: 'Punto de Espíritu ganado',
    file: 'skill/passive_point',
    volume: 0.7,
  },
  // Flechas (FUTURO: sin sistema de proyectiles todavía). Registradas para cuando exista.
  sfx_arrow_impact: {
    id: 'sfx_arrow_impact',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Impacto de flecha',
    file: 'combat/arrow_impact/1',
    variants: ['combat/arrow_impact/2', 'combat/arrow_impact/3'],
  },
  sfx_arrow_miss: {
    id: 'sfx_arrow_miss',
    category: SoundCategory.COMBAT,
    group: 'Combate',
    label: 'Fallo de flecha',
    file: 'combat/arrow_miss/1',
    variants: ['combat/arrow_miss/2', 'combat/arrow_miss/3'],
  },

  // --- Loot ---
  // Ítem ÚNICO en el piso: LOOP posicional mientras el ítem esté en el suelo (arranca al aparecer,
  // para al levantarse). Ver GroundItemsController + docs/SOUND_AND_MUSIC.md.
  loot_unique_floor_loop: {
    id: 'loot_unique_floor_loop',
    category: SoundCategory.AMBIENT,
    group: 'Loot',
    label: 'Ítem único en el piso (loop)',
    file: 'loot/unique_floor_loop',
    loop: true,
    // Volumen MUY bajo a propósito (0.6 → 0.3 → 0.15, dos bajadas del 50%): el loop debe ser
    // apenas un susurro que delata al único sin molestar.
    volume: 0.15,
  },

  // --- NPC muerte/hit (defaults) ---
  sfx_npc_death: {
    id: 'sfx_npc_death',
    category: SoundCategory.NPC,
    group: 'NPCs',
    label: 'NPC muere (default)',
    file: 'npc/death',
  },
  sfx_npc_hit: {
    id: 'sfx_npc_hit',
    category: SoundCategory.NPC,
    group: 'NPCs',
    label: 'NPC golpeado (default)',
    file: 'npc/hit',
  },

  // --- Monedas (al ganar oro; tier por cantidad, con variantes) ---
  coin_small: {
    id: 'coin_small', category: SoundCategory.UI, group: 'Monedas', label: 'Oro — poco',
    file: 'coins/small_1', variants: ['coins/small_2'], volume: 0.6,
  },
  coin_gentle: {
    id: 'coin_gentle', category: SoundCategory.UI, group: 'Monedas', label: 'Oro — algo',
    file: 'coins/gentle_1', variants: ['coins/gentle_2'], volume: 0.6,
  },
  coin_big: {
    id: 'coin_big', category: SoundCategory.UI, group: 'Monedas', label: 'Oro — bastante',
    file: 'coins/big_1', variants: ['coins/big_2', 'coins/big_3'], volume: 0.65,
  },
  coin_huge: {
    id: 'coin_huge', category: SoundCategory.UI, group: 'Monedas', label: 'Oro — mucho',
    file: 'coins/huge_1', variants: ['coins/huge_2', 'coins/huge_3', 'coins/huge_4'], volume: 0.7,
  },

  // --- Equipar / levantar ropa (por tipo de armadura) ---
  equip_clothes_heavy: {
    id: 'equip_clothes_heavy', category: SoundCategory.UI, group: 'Equipo', label: 'Equipar — pesada',
    file: 'equip/clothes_1', volume: 0.6,
  },
  equip_clothes_light: {
    id: 'equip_clothes_light', category: SoundCategory.UI, group: 'Equipo', label: 'Equipar — ligera',
    file: 'equip/clothes_2', volume: 0.6,
  },
  equip_clothes_cloth: {
    id: 'equip_clothes_cloth', category: SoundCategory.UI, group: 'Equipo', label: 'Equipar — tela',
    file: 'equip/clothes_3', volume: 0.6,
  },
  equip_default: {
    id: 'equip_default', category: SoundCategory.UI, group: 'Equipo', label: 'Equipar (default)',
    file: 'equip/clothes_4', volume: 0.6,
  },
  pickup_clothes_heavy: {
    id: 'pickup_clothes_heavy', category: SoundCategory.UI, group: 'Equipo', label: 'Levantar — pesada',
    file: 'pickup/clothes_1', volume: 0.6,
  },
  pickup_clothes_light: {
    id: 'pickup_clothes_light', category: SoundCategory.UI, group: 'Equipo', label: 'Levantar — ligera',
    file: 'pickup/clothes_2', volume: 0.6,
  },
  pickup_clothes_cloth: {
    id: 'pickup_clothes_cloth', category: SoundCategory.UI, group: 'Equipo', label: 'Levantar — tela',
    file: 'pickup/clothes_3', volume: 0.6,
  },

  // --- Ambiente de zona (loops; se asignan a áreas desde el editor) ---
  ambient_rain_heavy: { id: 'ambient_rain_heavy', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Lluvia fuerte', file: 'ambient/rain_heavy', loop: true, volume: 0.5 },
  ambient_rain_medium: { id: 'ambient_rain_medium', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Lluvia media', file: 'ambient/rain_medium', loop: true, volume: 0.5 },
  ambient_rain_light: { id: 'ambient_rain_light', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Lluvia suave', file: 'ambient/rain_light', loop: true, volume: 0.5 },
  ambient_river: { id: 'ambient_river', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Río', file: 'ambient/river', loop: true, volume: 0.5 },
  ambient_room_noise: { id: 'ambient_room_noise', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Interior (room noise)', file: 'ambient/room_noise', loop: true, volume: 0.4 },
  ambient_dark_1: { id: 'ambient_dark_1', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Oscuridad 1', file: 'ambient/dark_1', loop: true, volume: 0.5 },
  ambient_dark_2: { id: 'ambient_dark_2', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Oscuridad 2', file: 'ambient/dark_2', loop: true, volume: 0.5 },
  ambient_lake: { id: 'ambient_lake', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Lago', file: 'ambient/lake', loop: true, volume: 0.5 },
  ambient_lava: { id: 'ambient_lava', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Lava', file: 'ambient/lava', loop: true, volume: 0.5 },
  ambient_ocean: { id: 'ambient_ocean', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Mar', file: 'ambient/ocean', loop: true, volume: 0.5 },
  ambient_wind_high: { id: 'ambient_wind_high', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Viento fuerte', file: 'ambient/wind_high', loop: true, volume: 0.5 },
  ambient_wind_low: { id: 'ambient_wind_low', category: SoundCategory.AMBIENT, group: 'Ambiente', label: 'Viento suave', file: 'ambient/wind_low', loop: true, volume: 0.5 },

  // --- Interacción (FUTURO: sin sistema aún, registrados para cuando existan) ---
  interact_door_open: { id: 'interact_door_open', category: SoundCategory.UI, group: 'Interacción', label: 'Puerta abre', file: 'interact/door_open' },
  interact_door_close: { id: 'interact_door_close', category: SoundCategory.UI, group: 'Interacción', label: 'Puerta cierra', file: 'interact/door_close' },
  interact_key_insert: { id: 'interact_key_insert', category: SoundCategory.UI, group: 'Interacción', label: 'Llave inserta', file: 'interact/key_insert' },
  interact_key_extract: { id: 'interact_key_extract', category: SoundCategory.UI, group: 'Interacción', label: 'Llave saca', file: 'interact/key_extract' },
  interact_key_lock: { id: 'interact_key_lock', category: SoundCategory.UI, group: 'Interacción', label: 'Traba', file: 'interact/key_lock' },
  interact_key_unlock: { id: 'interact_key_unlock', category: SoundCategory.UI, group: 'Interacción', label: 'Destraba', file: 'interact/key_unlock' },
  interact_switch_on: { id: 'interact_switch_on', category: SoundCategory.UI, group: 'Interacción', label: 'Switch on', file: 'interact/switch_on' },
  interact_switch_off: { id: 'interact_switch_off', category: SoundCategory.UI, group: 'Interacción', label: 'Switch off', file: 'interact/switch_off' },

  // --- Música (pistas por fase/zona; loopean con crossfade) ---
  music_intro: {
    id: 'music_intro',
    category: SoundCategory.MUSIC,
    group: 'Música',
    label: 'Intro / carga',
    file: 'music/intro',
    loop: true,
    volume: 0.7,
  },
  music_createchar: {
    id: 'music_createchar',
    category: SoundCategory.MUSIC,
    group: 'Música',
    label: 'Creación de personaje',
    file: 'music/createchar',
    loop: true,
    volume: 0.7,
  },
  music_city: {
    id: 'music_city',
    category: SoundCategory.MUSIC,
    group: 'Música',
    label: 'Ciudad',
    file: 'music/city',
    loop: true,
    volume: 0.6,
  },
  music_forest: {
    id: 'music_forest',
    category: SoundCategory.MUSIC,
    group: 'Música',
    label: 'Bosque',
    file: 'music/forest',
    loop: true,
    volume: 0.6,
  },

  // --- Heartbeat ---
  sfx_heartbeat: {
    id: 'sfx_heartbeat',
    category: SoundCategory.HEARTBEAT,
    group: 'Estado',
    label: 'Latido (vida baja)',
    file: 'state/heartbeat',
    loop: true,
  },
};

// ============================================================================
// TABLAS DE MAPEO (default + override opcional)
// ============================================================================

/** Sonido de paso por defecto (mundo abierto) cuando el subtipo no tiene mapeo propio. */
export const DEFAULT_FOOTSTEP_SOUND = 'footstep_grass';

/**
 * `SurfaceDefinition.subtype` → sonido de paso (por terreno). Se resuelve contra el **subtype** del
 * surface pisado (ver MapLoader/FootstepSystem). Al no matchear cae a DEFAULT_FOOTSTEP_SOUND.
 * Mapeo pedido: grass→pasto, dirt→tierra, path→piedra, floor/house→madera, biomas de dungeon→piedra,
 * sand→tierra, snow→tierra (placeholder), water→agua (pendiente).
 */
export const SURFACE_FOOTSTEP_SOUNDS: Record<string, string> = {
  grass: 'footstep_grass',
  dirt: 'footstep_dirt',
  sand: 'footstep_dirt',
  snow: 'footstep_dirt',
  path: 'footstep_stone',
  floor: 'footstep_wood',
  house: 'footstep_wood',
  water: 'footstep_water',
  // Pisos de dungeon genéricos → piedra.
  dungeon1: 'footstep_stone',
  dungeon2: 'footstep_stone',
  // Biomas de floor de dungeon → piedra.
  cavoryn: 'footstep_stone',
  crysalis: 'footstep_stone',
  morassis: 'footstep_stone',
  pyrion: 'footstep_stone',
  sylvaris: 'footstep_stone',
  terranor: 'footstep_stone',
  umbraxis: 'footstep_stone',
  veriil: 'footstep_stone',
};

/** Sonido de agua específico al caminar/navegar sobre agua (independiente del subtype). Pendiente. */
export const WATER_FOOTSTEP_SOUND = 'footstep_water';

/**
 * Subtipo de armadura del RECEPTOR (body equipado) → sonido de impacto al recibir un golpe.
 * También lo usa el escudo (bloqueo) por su subtype. Default al impacto genérico.
 */
export const ARMOR_HIT_SOUNDS: Record<ArmorSubtype, string> = {
  [ArmorSubtype.HEAVY]: 'sfx_impact_heavy',
  [ArmorSubtype.LIGHT]: 'sfx_impact_light',
  [ArmorSubtype.CLOTH]: 'sfx_impact_cloth',
};

/**
 * Subtipo del ESCUDO equipado del que bloquea → sonido de bloqueo. Los escudos de metal (heavy)
 * suenan metálicos; el resto usa el default. Default `sfx_shield_block`.
 */
export const SHIELD_BLOCK_SOUNDS: Record<ArmorSubtype, string> = {
  [ArmorSubtype.HEAVY]: 'shield_metal',
  [ArmorSubtype.LIGHT]: 'sfx_shield_block',
  [ArmorSubtype.CLOTH]: 'sfx_shield_block',
};
export const SHIELD_BLOCK_DEFAULT_SOUND = 'sfx_shield_block';

/**
 * Subtipo de arma → sonido de golpe (override del default melee/ranged). Vacío por ahora: todas las
 * armas caen al default de su categoría hasta cargar SFX propios. Al sumar una, agregar su id acá.
 */
export const WEAPON_SUBTYPE_HIT_SOUNDS: Partial<Record<WeaponSubtype, string>> = {};

/** Subtipos de arma considerados "a distancia" para elegir el default ranged vs melee. */
export const RANGED_WEAPON_SUBTYPES: ReadonlySet<WeaponSubtype> = new Set([
  WeaponSubtype.BOW,
  WeaponSubtype.CROSSBOW,
  WeaponSubtype.THROWABLE,
]);

/**
 * Sonidos de flecha por "tipo" de munición (FUTURO: no hay sistema de flechas todavía). Stub con
 * default; el consumidor real aparece cuando exista el sistema de proyectiles con munición.
 */
export const ARROW_TYPE_SOUNDS: Record<string, string> = {};
export const DEFAULT_ARROW_SOUND = 'sfx_combat_ranged';

/** Defaults de combate y eventos globales. */
export const COMBAT_DEFAULTS = {
  melee: 'sfx_combat_melee',
  ranged: 'sfx_combat_ranged',
  miss: 'combat_miss',
  impactDefault: 'sfx_combat_impact',
  shieldBlock: 'sfx_shield_block',
  npcDeath: 'sfx_npc_death',
  npcHit: 'sfx_npc_hit',
  heartbeat: 'sfx_heartbeat',
  pickaxe: 'sfx_pickaxe_mining',
} as const;

/** Resuelve el sonido de bloqueo por subtipo de escudo (o default). */
export function shieldBlockSound(shieldSubtype: string | undefined): string {
  if (shieldSubtype && shieldSubtype in SHIELD_BLOCK_SOUNDS) {
    return SHIELD_BLOCK_SOUNDS[shieldSubtype as ArmorSubtype];
  }
  return SHIELD_BLOCK_DEFAULT_SOUND;
}

/** Resuelve el sonido de impacto por subtipo de armadura del receptor (o default). */
export function armorHitSound(armorSubtype: string | undefined): string {
  if (armorSubtype && armorSubtype in ARMOR_HIT_SOUNDS) {
    return ARMOR_HIT_SOUNDS[armorSubtype as ArmorSubtype];
  }
  return COMBAT_DEFAULTS.impactDefault;
}

/** Loop posicional del ítem único en el piso (start/stop por GroundItemsController). */
export const UNIQUE_FLOOR_LOOP_SOUND = 'loot_unique_floor_loop';

/**
 * Sonido de moneda al GANAR oro, por tramo de cantidad (delta>0). El primer tramo cuyo `maxDelta`
 * es ≥ al oro ganado define el sonido. El último (`Infinity`) es el tope. Ver GameScene onGoldUpdated.
 */
export const GOLD_COIN_TIERS: ReadonlyArray<{ maxDelta: number; sound: string }> = [
  { maxDelta: 50, sound: 'coin_small' },
  { maxDelta: 250, sound: 'coin_gentle' },
  { maxDelta: 1000, sound: 'coin_big' },
  { maxDelta: Infinity, sound: 'coin_huge' },
];

/** Devuelve el id de sonido de moneda para una ganancia de oro. */
export function goldCoinSound(delta: number): string {
  for (const t of GOLD_COIN_TIERS) {
    if (delta <= t.maxDelta) return t.sound;
  }
  return 'coin_huge';
}

/** Subtipo de armadura del ítem → sonido al EQUIPARLO. Default para no-armadura o subtype ausente. */
export const EQUIP_SOUNDS: Record<ArmorSubtype, string> = {
  [ArmorSubtype.HEAVY]: 'equip_clothes_heavy',
  [ArmorSubtype.LIGHT]: 'equip_clothes_light',
  [ArmorSubtype.CLOTH]: 'equip_clothes_cloth',
};
export const EQUIP_DEFAULT_SOUND = 'equip_default';

/** Subtipo de armadura del ítem → sonido al LEVANTARLO del piso. Default = tela. */
export const PICKUP_SOUNDS: Record<ArmorSubtype, string> = {
  [ArmorSubtype.HEAVY]: 'pickup_clothes_heavy',
  [ArmorSubtype.LIGHT]: 'pickup_clothes_light',
  [ArmorSubtype.CLOTH]: 'pickup_clothes_cloth',
};
export const PICKUP_DEFAULT_SOUND = 'pickup_clothes_cloth';

// ============================================================================
// CONFIG DE MÚSICA / ESTADO (valores por defecto)
// ============================================================================

/** Bajo este % de HP la música se acelera. */
export const MUSIC_LOWHP_THRESHOLD = 0.3;
/** Factor de aceleración de la música con vida baja. */
export const MUSIC_LOWHP_RATE = 1.35;
/** Bajo este % de HP arranca el heartbeat. */
export const HEARTBEAT_HP_THRESHOLD = 0.15;

// ============================================================================
// SCOPE DE BROADCAST (visor admin / PLAY_SOUND)
// ============================================================================

/** Alcance de un PLAY_SOUND disparado por un admin desde el visor de sonidos. */
export type SoundBroadcastScope = 'screen' | 'zone' | 'map' | 'all';

// ============================================================================
// TIPOS DE CONFIG DE SONIDO POR CONTENIDO (referenciados desde otros catálogos)
// ============================================================================

/** Sonidos opcionales de un hechizo (referenciado desde SpellDefinition.sounds). */
export interface SpellSounds {
  /** Al comenzar el casteo (spells con castTime > 0). */
  castStart?: string;
  /** Loop mientras dura el casteo. */
  castLoop?: string;
  /** Al terminar el casteo / disparo instantáneo. */
  castEnd?: string;
  /** Al impactar en un objetivo. */
  impact?: string;
  /** Al matar a un jugador con este hechizo. */
  killPlayer?: string;
  /** Al matar a un NPC con este hechizo. */
  killNpc?: string;
}

/** Sonidos opcionales de un NPC (referenciado desde NPCTemplateDefinition.sounds). */
export interface NpcSounds {
  /** Al interactuar/abrir su diálogo o ventana. */
  greet?: string;
  /** Al concretar una compra/venta (merchant). */
  trade?: string;
  /** Al revivir (sacerdote). */
  revive?: string;
  /** Al enseñar un hechizo con éxito (spell vendor). */
  teachSpell?: string;
  /** Al intentar aprender un hechizo que no se puede. */
  learnFail?: string;
  /** Al morir (override del default). */
  onDeath?: string;
  /** Al ser golpeado (override del default). */
  onHit?: string;
}

/** Config de ruidos de entorno aleatorios de un área (referenciado desde MapAreaData). */
export interface AreaAmbientSounds {
  /** IDs de sonidos candidatos; en cada disparo se elige uno al azar. */
  ids: string[];
  /** Intervalo mínimo entre disparos (ms). */
  minMs: number;
  /** Intervalo máximo entre disparos (ms). */
  maxMs: number;
}

/** True si el id existe en el registro. Barato; usado por el manager y validaciones. */
export function isKnownSound(id: string | undefined | null): id is string {
  return typeof id === 'string' && Object.prototype.hasOwnProperty.call(SOUNDS, id);
}
